/**
 * Admin console API client (M2 W5b). Thin typed wrappers over `fetchEnvelope` for the
 * `/api/v1/admin/*` console API (W5a). Every call reuses the shared fetch wrapper so the
 * Basic-auth header, the X-Correlation-Id, the {data,meta,traceId} unwrap, and the
 * RFC 9457 problem+json -> ApiProblem handling are identical to the rest of the app.
 *
 * The DTO shapes mirror the SAFE projections the service returns: NEVER a `passwordHash`,
 * NEVER a raw secret (instances carry only `connectionSecretRef`, an env-var POINTER).
 * Timestamps arrive as ISO strings over JSON. This module holds NO PHI (operator
 * identities + registry config + non-PHI audit rows only).
 */
import { fetchEnvelope } from "./api";
import type { Meta } from "@shared/contracts";
import {
  adminTenantsSchema,
  adminInstancesSchema,
  adminUsersSchema,
  adminUserDetailSchema,
  adminRolesSchema,
  adminAuditLogSchema,
} from "./schemas/admin";

/** JSON-mutation helper: sets the JSON content-type, serializes the body, returns the unwrapped envelope. */
function mutate<T>(path: string, method: "POST" | "PATCH" | "DELETE", body?: unknown) {
  return fetchEnvelope<T>(path, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

// ── Tenants ──────────────────────────────────────────────────────────────────
export interface AdminTenant {
  id: string;
  slug: string;
  displayName: string;
  domain: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}
export interface CreateTenantBody {
  slug: string;
  displayName: string;
  domain?: string | null;
}
export interface UpdateTenantBody {
  displayName?: string;
  status?: "active" | "inactive" | "disabled";
}

export const listTenants = () =>
  fetchEnvelope<AdminTenant[]>("/api/v1/admin/tenants", undefined, adminTenantsSchema);
export const createTenant = (body: CreateTenantBody) =>
  mutate<AdminTenant>("/api/v1/admin/tenants", "POST", body);
export const updateTenant = (id: string, body: UpdateTenantBody) =>
  mutate<AdminTenant>(`/api/v1/admin/tenants/${id}`, "PATCH", body);

// ── Instances ────────────────────────────────────────────────────────────────
export interface AdminInstance {
  id: string;
  tenantId: string;
  name: string;
  label: string;
  environment: "dev" | "prod";
  dbHost: string;
  dbPort: number;
  dbName: string;
  dbUser: string;
  lakebaseProjectId: string;
  lakebaseBranch: string;
  lakebaseEndpoint: string;
  /** A POINTER to a secret-manager entry / env-var name, NEVER a secret value. */
  connectionSecretRef: string | null;
  status: "active" | "provisioning" | "disabled";
  createdAt: string;
  updatedAt: string;
}
export interface CreateInstanceBody {
  tenantId: string;
  name: string;
  label: string;
  environment: "dev" | "prod";
  dbHost: string;
  dbPort?: number;
  dbName: string;
  dbUser: string;
  lakebaseProjectId: string;
  lakebaseBranch: string;
  lakebaseEndpoint: string;
  connectionSecretRef?: string | null;
  status?: "active" | "provisioning" | "disabled";
}
export interface UpdateInstanceBody {
  label?: string;
  status?: "active" | "provisioning" | "disabled";
  connectionSecretRef?: string | null;
}

export const listInstances = (tenantId?: string) =>
  fetchEnvelope<AdminInstance[]>(
    `/api/v1/admin/instances${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ""}`,
    undefined,
    adminInstancesSchema,
  );
export const createInstance = (body: CreateInstanceBody) =>
  mutate<AdminInstance>("/api/v1/admin/instances", "POST", body);
export const updateInstance = (id: string, body: UpdateInstanceBody) =>
  mutate<AdminInstance>(`/api/v1/admin/instances/${id}`, "PATCH", body);

// ── Users + role assignments ─────────────────────────────────────────────────
/** A user WITHOUT the password hash, the only user shape the API ever returns. */
export interface AdminUser {
  id: string;
  email: string;
  fullName: string | null;
  status: string;
  azureB2cId: string | null;
  lastInstanceId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface UserRoleAssignment {
  id: string;
  roleId: string;
  roleName: string;
  roleLabel: string;
  tenantId: string | null;
  instanceId: string | null;
  createdAt: string;
}
export type AdminUserDetail = AdminUser & { roles: UserRoleAssignment[] };

export interface CreateUserBody {
  email: string;
  fullName?: string | null;
  password: string;
  status?: "pending" | "active" | "blocked";
}
export interface UpdateUserBody {
  email?: string;
  fullName?: string | null;
  status?: "pending" | "active" | "blocked";
}
export interface GrantRoleBody {
  roleId: string;
  tenantId?: string | null;
  instanceId?: string | null;
}

export const listUsers = () =>
  fetchEnvelope<AdminUser[]>("/api/v1/admin/users", undefined, adminUsersSchema);
export const getUser = (id: string) =>
  fetchEnvelope<AdminUserDetail>(`/api/v1/admin/users/${id}`, undefined, adminUserDetailSchema);
export const createUser = (body: CreateUserBody) =>
  mutate<AdminUser>("/api/v1/admin/users", "POST", body);
export const updateUser = (id: string, body: UpdateUserBody) =>
  mutate<AdminUser>(`/api/v1/admin/users/${id}`, "PATCH", body);
export const grantUserRole = (id: string, body: GrantRoleBody) =>
  mutate<UserRoleAssignment>(`/api/v1/admin/users/${id}/roles`, "POST", body);
export const revokeUserRole = (id: string, assignmentId: string) =>
  mutate<{ revoked: boolean; assignmentId: string }>(
    `/api/v1/admin/users/${id}/roles/${assignmentId}`,
    "DELETE",
  );

// ── Roles (read-only) ────────────────────────────────────────────────────────
export interface AdminRole {
  id: string;
  name: string;
  label: string;
  description: string | null;
  category: string;
  vertical: string;
  tenantId: string | null;
  isSystem: boolean;
  isHidden: boolean;
  isImmutable: boolean;
  permissions: string[];
}
export const listRoles = () =>
  fetchEnvelope<AdminRole[]>("/api/v1/admin/roles", undefined, adminRolesSchema);

// ── Audit log (read-only, non-PHI) ───────────────────────────────────────────
export interface AuditRow {
  id: string;
  actorEmail: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  tenantId: string | null;
  instanceId: string | null;
  traceId: string | null;
  metadata: unknown;
  createdAt: string;
}
export interface AuditFilters {
  limit: number;
  offset: number;
  actorEmail?: string;
  action?: string;
  resourceId?: string;
  from?: string;
  to?: string;
}
export function listAuditLog(filters: AuditFilters): Promise<{ data: AuditRow[]; meta?: Meta }> {
  const params = new URLSearchParams({
    limit: String(filters.limit),
    offset: String(filters.offset),
  });
  if (filters.actorEmail) params.set("actorEmail", filters.actorEmail);
  if (filters.action) params.set("action", filters.action);
  if (filters.resourceId) params.set("resourceId", filters.resourceId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  return fetchEnvelope<AuditRow[]>(
    `/api/v1/admin/audit-log?${params.toString()}`,
    undefined,
    adminAuditLogSchema,
  );
}
